/**
 * PlayerListSubmenu Container Component
 *
 * Manages theme-based styling logic for the player list submenu.
 * @module components/HeaderCreateMenu/PlayerListSubmenu
 */

import React, { useMemo } from "react";

import PlayerListSubmenuView from "./PlayerListSubmenuView";

/**
 * Recent player item for display in menus
 */
interface RecentPlayerItem {
  id: string;
  name: string;
}

/**
 * Get theme-based class names for player list submenu
 * @param isDark - Whether dark theme is active
 */
const getThemeClasses = (isDark: boolean) => ({
  playerList: "",
  sectionLabel:
    "px-4 pt-2 pb-1 pl-12 text-2xs font-medium uppercase tracking-wider text-content-subtle",
  emptyText: `px-4 py-3 pl-12 text-body text-content-subtle`,
  playerItem: `flex-row items-center gap-3 rounded-sm px-4 py-2 pl-12 web:hover:bg-surface-inverse/5 ${isDark ? "active:bg-outline-emphasis" : "active:bg-surface-muted"}`,
  playerIconBox: `rounded-full p-1 ${isDark ? "bg-surface-raised" : "bg-needs-semantic-secondary-100"}`,
  playerIcon: isDark ? "text-content-subtle" : "text-content-secondary",
  playerName: `text-body font-medium ${isDark ? "text-needs-semantic-typography-800" : "text-surface-inverse"}`,
});

/**
 * Props for PlayerListSubmenu component
 */
interface PlayerListSubmenuProps {
  players: readonly RecentPlayerItem[];
  onSelectPlayer: (playerId: string) => void;
  isDark: boolean;
}

/**
 * Container component managing theme styling for player list submenu.
 * @param root0 - Component props
 * @param root0.players - Array of recent player items to display
 * @param root0.onSelectPlayer - Callback when a player is selected
 * @param root0.isDark - Whether dark theme is active
 */
const PlayerListSubmenuContainer = ({
  players,
  onSelectPlayer,
  isDark,
}: PlayerListSubmenuProps) => {
  const classes = useMemo(() => getThemeClasses(isDark), [isDark]);

  return (
    <PlayerListSubmenuView
      players={players}
      onSelectPlayer={onSelectPlayer}
      playerListClassName={classes.playerList}
      sectionLabelClassName={classes.sectionLabel}
      emptyTextClassName={classes.emptyText}
      playerItemClassName={classes.playerItem}
      playerIconBoxClassName={classes.playerIconBox}
      playerIconClassName={classes.playerIcon}
      playerNameClassName={classes.playerName}
    />
  );
};

export default PlayerListSubmenuContainer;
